import { useState } from 'react';
import { useMarketingData } from '../MarketingContext';
import { TaskStatus } from '../types';

export default function TasksPage() {
  const { companies, projects, tasks, updateTask } = useMarketingData();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [selected, setSelected] = useState<typeof tasks[0] | null>(null);

  const statusColors: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-600', in_progress: 'bg-blue-100 text-blue-700',
    blocked: 'bg-red-100 text-red-700', waiting_approval: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700', cancelled: 'bg-slate-100 text-slate-500',
  };
  const priorityColors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700',
    medium: 'bg-blue-100 text-blue-700', low: 'bg-gray-100 text-gray-500',
  };

  const filtered = tasks.filter(t => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    return true;
  });

  const openTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length;
  const overdueTasks = tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'completed').length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;

  if (selected) {
    const co = companies.find(c => c.id === selected.companyId);
    const proj = projects.find(p => p.id === selected.projectId);
    return (
      <div className="space-y-4">
        <button onClick={() => setSelected(null)} className="text-sm text-blue-600 hover:underline">← Back to Tasks</button>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selected.title}</h2>
              <p className="text-sm text-gray-500 mt-1">{selected.description}</p>
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[selected.status]}`}>{selected.status.replace('_', ' ')}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${priorityColors[selected.priority]}`}>{selected.priority}</span>
                <span className="text-xs text-gray-400">🏢 {co?.name} · 📋 {proj?.name}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div><span className="text-xs text-gray-500">Assigned to:</span><div className="text-sm font-medium text-gray-800">{selected.assignedTo}</div></div>
            <div><span className="text-xs text-gray-500">Due date:</span><div className="text-sm font-medium text-gray-800">{selected.dueDate}</div></div>
          </div>
          {/* Checklist */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Checklist</h3>
            <div className="space-y-1.5">
              {selected.checklist.map(item => (
                <label key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input type="checkbox" checked={item.completed} onChange={() => {
                    const newChecklist = selected.checklist.map(c => c.id === item.id ? { ...c, completed: !c.completed } : c);
                    updateTask(selected.id, { checklist: newChecklist });
                    setSelected({ ...selected, checklist: newChecklist });
                  }} className="h-4 w-4 rounded border-gray-300" />
                  <span className={`text-sm ${item.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{item.text}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Status Actions */}
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(statusColors).filter(s => s !== selected.status).map(s => (
              <button key={s} onClick={() => { updateTask(selected.id, { status: s as TaskStatus }); setSelected({ ...selected, status: s as TaskStatus }); }} className={`text-xs px-2.5 py-1 rounded-full ${statusColors[s]}`}>→ {s.replace('_', ' ')}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold text-gray-900">Tasks & Approvals</h1><p className="text-sm text-gray-500">Track marketing tasks, approvals, and deadlines</p></div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4"><div className="text-xs text-blue-600 font-medium">Open Tasks</div><div className="text-2xl font-bold text-blue-700">{openTasks}</div></div>
        <div className="bg-white border border-gray-200 rounded-xl p-4"><div className="text-xs text-red-600 font-medium">Overdue</div><div className="text-2xl font-bold text-red-700">{overdueTasks}</div></div>
        <div className="bg-white border border-gray-200 rounded-xl p-4"><div className="text-xs text-green-600 font-medium">Completed</div><div className="text-2xl font-bold text-green-700">{completedTasks}</div></div>
      </div>

      <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Status</option>{Object.keys(statusColors).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Priority</option>{Object.keys(priorityColors).map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {filtered.map(t => {
          const completedCount = t.checklist.filter(c => c.completed).length;
          const totalCount = t.checklist.length;
          const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
          const isOverdue = new Date(t.dueDate) < new Date() && t.status !== 'completed';
          return (
            <button key={t.id} onClick={() => setSelected(t)} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-left hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-sm font-bold text-gray-900">{t.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t.description.slice(0, 100)}...</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[t.status]}`}>{t.status.replace('_', ' ')}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${priorityColors[t.priority]}`}>{t.priority}</span>
                    <span className="text-xs text-gray-400">👤 {t.assignedTo}</span>
                    <span className={`text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>📅 {t.dueDate}</span>
                  </div>
                </div>
                {totalCount > 0 && (
                  <div className="text-right ml-4">
                    <div className="text-xs text-gray-500 mb-1">{completedCount}/{totalCount}</div>
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-green-400 rounded-full" style={{ width: `${progress}%` }} /></div>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
